import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchKnowledgeGraphEdges, fetchKnowledgeGraphNodes } from '@/lib/librarian/api';
import { initializeVault } from '@/lib/librarian/initializeVault';
import type { KnowledgeGraphEdge, KnowledgeGraphNode, KnowledgeGraphNodeType } from '@/lib/librarian/types';
import { toast } from '@/components/ui/use-toast';

export type KnowledgeGraphFilters = {
  nodeTypes: KnowledgeGraphNodeType[];
  edgeTypes: string[];
  search: string;
};

export type KnowledgeGraphVisualNode = {
  id: string;
  label: string;
  type: KnowledgeGraphNodeType;
  position: { x: number; y: number };
  raw: KnowledgeGraphNode;
};

const defaultNodeTypeOrder: KnowledgeGraphNodeType[] = ['source', 'page', 'topic', 'entity', 'run'];
const defaultEdgeTypes = [
  'wiki_link',
  'references_source',
  'mentions',
  'related_to',
  'generated_by_run',
  'processed_source',
  'derived_from_page',
  'co_mentions',
];

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function positionNodes(nodes: KnowledgeGraphNode[], layoutWidth = 1600, layoutHeight = 920) {
  const grouped = new Map<KnowledgeGraphNodeType, KnowledgeGraphNode[]>();
  for (const type of defaultNodeTypeOrder) grouped.set(type, []);

  for (const node of nodes) {
    grouped.set(node.node_type, [...(grouped.get(node.node_type) ?? []), node]);
  }

  const layout: KnowledgeGraphVisualNode[] = [];
  const columnCount = defaultNodeTypeOrder.length;
  const safeWidth = Math.max(layoutWidth, 1200);
  const safeHeight = Math.max(layoutHeight, 720);
  const horizontalPadding = clamp(safeWidth * 0.08, 120, 220);
  const verticalPadding = clamp(safeHeight * 0.1, 70, 150);
  const availableWidth = Math.max(safeWidth - horizontalPadding * 2, 1480);
  const availableHeight = Math.max(safeHeight - verticalPadding * 2, 560);
  const xSpacing = columnCount > 1
    ? clamp(availableWidth / (columnCount - 1), 460, 760)
    : 0;

  const startX = -((columnCount - 1) * xSpacing) / 2;
  const preferredRowsPerLane = clamp(Math.floor(availableHeight / 110), 4, 7);
  const maxLanesPerType = 2;

  defaultNodeTypeOrder.forEach((type, columnIndex) => {
    const group = (grouped.get(type) ?? []).sort((a, b) => a.label.localeCompare(b.label));

    if (group.length === 0) {
      return;
    }

    const laneCount = Math.min(
      group.length,
      Math.max(1, Math.min(maxLanesPerType, Math.ceil(group.length / preferredRowsPerLane)))
    );
    const rowsPerLane = Math.ceil(group.length / laneCount);
    const ySpacing = rowsPerLane > 1
      ? clamp(availableHeight / (rowsPerLane - 1), 82, 158)
      : 0;
    const startY = -((rowsPerLane - 1) * ySpacing) / 2;
    const laneSpacing = laneCount > 1 ? clamp(xSpacing * 0.42, 220, 320) : 0;

    const baseX = startX + columnIndex * xSpacing;
    const laneStartX = baseX - ((laneCount - 1) * laneSpacing) / 2;

    group.forEach((node, nodeIndex) => {
      const laneIndex = Math.floor(nodeIndex / rowsPerLane);
      const rowIndex = nodeIndex % rowsPerLane;

      layout.push({
        id: node.id,
        label: node.label,
        type,
        position: {
          x: laneStartX + laneIndex * laneSpacing,
          y: startY + rowIndex * ySpacing,
        },
        raw: node,
      });
    });
  });

  return layout;
}

export function useKnowledgeGraph(
  knowledgeBaseId?: string | null,
  refreshKey?: string | number,
  layoutWidth?: number,
  layoutHeight?: number,
) {
  const [nodes, setNodes] = useState<KnowledgeGraphNode[]>([]);
  const [edges, setEdges] = useState<KnowledgeGraphEdge[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!knowledgeBaseId) {
      setNodes([]);
      setEdges([]);
      return;
    }

    setLoading(true);
    try {
      let [nextNodes, nextEdges] = await Promise.all([
        fetchKnowledgeGraphNodes(knowledgeBaseId),
        fetchKnowledgeGraphEdges(knowledgeBaseId),
      ]);

      if (nextNodes.length === 0) {
        await initializeVault(knowledgeBaseId);
        [nextNodes, nextEdges] = await Promise.all([
          fetchKnowledgeGraphNodes(knowledgeBaseId),
          fetchKnowledgeGraphEdges(knowledgeBaseId),
        ]);
      }

      setNodes(nextNodes);
      setEdges(nextEdges);
    } catch (error) {
      console.error('Failed to fetch knowledge graph:', error);
      toast({ title: 'Error', description: 'Failed to load the knowledge graph.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [knowledgeBaseId]);

  useEffect(() => {
    void refresh();
  }, [refresh, refreshKey]);

  const defaultFilters = useMemo<KnowledgeGraphFilters>(() => ({
    nodeTypes: [...defaultNodeTypeOrder],
    edgeTypes: [...defaultEdgeTypes],
    search: '',
  }), []);

  const visualNodes = useMemo(
    () => positionNodes(nodes, layoutWidth, layoutHeight),
    [layoutHeight, layoutWidth, nodes]
  );

  return {
    nodes,
    edges,
    visualNodes,
    loading,
    refresh,
    defaultFilters,
  };
}